import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActivityIndicator,
    Modal,
    StyleSheet,
    TouchableOpacity,
    View,
} from 'react-native';
// `Alert` daqui, nao do react-native: no navegador o Alert do RN e um stub
// vazio e engole tudo em silencio (ver src/utils/appAlert.js). Em nativo e
// reexportacao direta - comportamento do app inalterado.
import { Alert } from '../../utils/appAlert';
import { Text } from '../ui/AppText';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { FadeInDown, FadeOutUp } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { cancelLiveDuel, joinLiveDuel } from '../../features/Challenge';

const LOOKUP_RETRY_DELAY_MS = 500;
const LOOKUP_MAX_ATTEMPTS = 8;
// O canal realtime (no useEffect abaixo) é o mecanismo PRIMÁRIO de convites.
// Este intervalo é apenas um BACKSTOP para eventos perdidos em reconexão do
// canal (o Supabase não reenvia eventos ocorridos durante a queda). Era 4s.
const INVITE_BACKSTOP_POLL_MS = 30000;

const isParticipant = (duel, userId) => duel?.player1_id === userId || duel?.player2_id === userId;

const getOpponentId = (duel, userId) => (
    duel?.player1_id === userId ? duel.player2_id : duel?.player1_id
);

const shouldShowReadyInvite = (duel, userId) => {
    if (!duel || !userId || !isParticipant(duel, userId)) return false;
    if (duel.status === 'cancelled' || duel.status === 'completed' || duel.live_started_at) return false;

    const isPlayer1 = duel.player1_id === userId;
    const currentUserReady = isPlayer1 ? duel.player1_ready : duel.player2_ready;
    const opponentReady = isPlayer1 ? duel.player2_ready : duel.player1_ready;

    return Boolean(opponentReady && !currentUserReady);
};

const LiveDuelInviteOverlay = memo(({ navigationRef }) => {
    const { user } = useAuth();
    const [invite, setInvite] = useState(null);
    const [isJoining, setIsJoining] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const shownChallengesRef = useRef(new Set());
    const shownReadyDuelsRef = useRef(new Set());
    const retryTimersRef = useRef(new Set());
    const inviteRef = useRef(null);

    useEffect(() => {
        inviteRef.current = invite;
    }, [invite]);

    const showDuelInvite = useCallback(async ({ challenge, duel, variant = 'accepted' }) => {
        if (!duel?.id || !user?.id) return;
        if (inviteRef.current?.duelId === duel.id) return;

        if (variant === 'accepted') {
            if (!challenge?.id || shownChallengesRef.current.has(challenge.id)) return;
            shownChallengesRef.current.add(challenge.id);
        } else {
            if (shownReadyDuelsRef.current.has(duel.id)) return;
            shownReadyDuelsRef.current.add(duel.id);
        }

        const opponentId = getOpponentId(duel, user.id);
        const { data: opponent } = await supabase
            .from('profiles')
            .select('id, nome, username, foto_url, nivel')
            .eq('id', opponentId)
            .maybeSingle();

        const nextInvite = {
            challengeId: challenge?.id || duel.challenge_id,
            duelId: duel.id,
            opponent,
            variant,
        };

        inviteRef.current = nextInvite;
        setInvite(nextInvite);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }, [user?.id]);

    const showAcceptedDuel = useCallback(async (challenge, duel) => {
        await showDuelInvite({ challenge, duel, variant: 'accepted' });
    }, [showDuelInvite]);

    const showReadyDuelIfNeeded = useCallback(async (duel) => {
        if (!shouldShowReadyInvite(duel, user?.id)) return;
        await showDuelInvite({
            challenge: { id: duel.challenge_id },
            duel,
            variant: 'ready',
        });
    }, [showDuelInvite, user?.id]);

    const fetchAcceptedDuel = useCallback(async (challenge, attempt = 0) => {
        if (!challenge?.id || shownChallengesRef.current.has(challenge.id)) return;
        if (inviteRef.current?.challengeId === challenge.id) return;

        const { data: duel } = await supabase
            .from('duels')
            .select('id, challenge_id, player1_id, player2_id, status, player1_ready, player2_ready, live_started_at')
            .eq('challenge_id', challenge.id)
            .maybeSingle();

        if (!duel) {
            if (attempt < LOOKUP_MAX_ATTEMPTS) {
                const timer = setTimeout(() => {
                    retryTimersRef.current.delete(timer);
                    fetchAcceptedDuel(challenge, attempt + 1);
                }, LOOKUP_RETRY_DELAY_MS);

                retryTimersRef.current.add(timer);
            }
            return;
        }

        if (duel.status === 'cancelled' || duel.live_started_at) return;

        await showAcceptedDuel(challenge, duel);
    }, [showAcceptedDuel]);

    const loadReadyInvites = useCallback(async () => {
        if (!user?.id || inviteRef.current) return;

        const { data } = await supabase
            .from('duels')
            .select('id, challenge_id, player1_id, player2_id, status, player1_ready, player2_ready, live_started_at, created_at')
            .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`)
            .in('status', ['in_progress', 'waiting_player2'])
            .is('live_started_at', null)
            .order('created_at', { ascending: false })
            .limit(5);

        for (const duel of data || []) {
            await showReadyDuelIfNeeded(duel);
            if (inviteRef.current) break;
        }
    }, [showReadyDuelIfNeeded, user?.id]);

    const loadAcceptedInvites = useCallback(async () => {
        if (!user?.id || inviteRef.current) return;

        const { data } = await supabase
            .from('duels_challenges')
            .select('id, challenged_id, status, updated_at')
            .eq('challenger_id', user.id)
            .eq('status', 'accepted')
            .order('updated_at', { ascending: false })
            .limit(3);

        for (const challenge of data || []) {
            await fetchAcceptedDuel(challenge);
            if (inviteRef.current) break;
        }
    }, [fetchAcceptedDuel, user?.id]);

    const loadGlobalInvites = useCallback(async () => {
        await loadReadyInvites();
        await loadAcceptedInvites();
    }, [loadAcceptedInvites, loadReadyInvites]);

    // Backstop poll + carga inicial. O canal realtime (abaixo, via grupo
    // compartilhado rt:invites) é o mecanismo PRIMÁRIO; este poll cobre eventos
    // perdidos durante reconexão do canal.
    useEffect(() => {
        if (!user?.id) return undefined;

        loadGlobalInvites();
        const pollTimer = setInterval(loadGlobalInvites, INVITE_BACKSTOP_POLL_MS);
        const retryTimers = retryTimersRef.current;

        return () => {
            clearInterval(pollTimer);
            retryTimers.forEach(clearTimeout);
            retryTimers.clear();
        };
    }, [loadGlobalInvites, user?.id]);

    // Bindings preservados verbatim do canal dedicado anterior, agora
    // multiplexados no canal compartilhado de convites.
    const realtimeBindings = useMemo(() => (
        user?.id
            ? [
                {
                    event: 'UPDATE', table: 'duels_challenges', filter: `challenger_id=eq.${user.id}`,
                    handler: (payload) => {
                        const challenge = payload.new;
                        if (challenge?.status !== 'accepted') return;
                        fetchAcceptedDuel(challenge);
                    },
                },
                {
                    event: 'UPDATE', table: 'duels', filter: `player1_id=eq.${user.id}`,
                    handler: (payload) => showReadyDuelIfNeeded(payload.new),
                },
                {
                    event: 'UPDATE', table: 'duels', filter: `player2_id=eq.${user.id}`,
                    handler: (payload) => showReadyDuelIfNeeded(payload.new),
                },
                {
                    event: 'INSERT', table: 'duels', filter: `player1_id=eq.${user.id}`,
                    handler: async (payload) => {
                        const duel = payload.new;
                        if (!duel?.challenge_id || duel.status === 'cancelled' || duel.live_started_at) return;

                        const { data: challenge } = await supabase
                            .from('duels_challenges')
                            .select('id, challenged_id, status, updated_at')
                            .eq('id', duel.challenge_id)
                            .maybeSingle();

                        if (challenge?.status === 'accepted') {
                            showAcceptedDuel(challenge, duel);
                        }
                    },
                },
            ]
            : []
    ), [user?.id, fetchAcceptedDuel, showReadyDuelIfNeeded, showAcceptedDuel]);

    useRealtimeSubscription(user?.id ? `rt:invites:${user.id}` : null, realtimeBindings, !!user?.id);

    const handleJoin = useCallback(async () => {
        if (!invite?.duelId || isJoining) return;

        setIsJoining(true);
        const result = await joinLiveDuel(invite.duelId);
        setIsJoining(false);

        if (!result.success) {
            Alert.alert('Nao foi possivel entrar', result.error || 'Tente novamente em alguns segundos.');
            return;
        }

        const duelId = invite.duelId;
        const routeName = result.data?.both_ready || result.data?.live_started_at ? 'DuelMatch' : 'DuelWaitingRoom';
        const routeParams = {
            duelId,
            challengeId: invite.challengeId,
            live: routeName === 'DuelMatch',
            role: invite.variant === 'accepted' ? 'challenger' : 'participant',
        };

        inviteRef.current = null;
        setInvite(null);
        if (navigationRef?.isReady?.()) {
            navigationRef.navigate(routeName, routeParams);
        } else {
            navigationRef?.current?.navigate?.(routeName, routeParams);
        }
    }, [invite, isJoining, navigationRef]);

    const handleCancel = useCallback(async () => {
        if (!invite?.duelId || isCancelling) return;

        setIsCancelling(true);
        const result = await cancelLiveDuel(invite.duelId);
        setIsCancelling(false);

        if (!result.success) {
            Alert.alert('Nao foi possivel recusar', result.error || 'Tente novamente.');
            return;
        }

        inviteRef.current = null;
        setInvite(null);
    }, [invite, isCancelling]);

    if (!invite) return null;

    const opponentName = invite.opponent?.nome || invite.opponent?.username || invite.opponent?.email?.split('@')[0] || 'Oponente';
    const isReadyInvite = invite.variant === 'ready';

    return (
        <Modal visible transparent animationType="none" statusBarTranslucent onRequestClose={handleCancel}>
            <View style={styles.overlay} pointerEvents="box-none">
                <Animated.View entering={FadeInDown.springify()} exiting={FadeOutUp} style={styles.toast}>
                    <LinearGradient
                        colors={['#2D1B4E', '#1A0B2E']}
                        style={StyleSheet.absoluteFill}
                    />

                    <View style={styles.headerRow}>
                        <View style={styles.liveDot} />
                        <Text style={styles.liveText}>{isReadyInvite ? 'OPONENTE PRONTO' : 'DUELO ACEITO'}</Text>
                    </View>

                    <View style={styles.contentRow}>
                        <View style={styles.avatarWrap}>
                            {invite.opponent?.foto_url ? (
                                <Image source={{ uri: invite.opponent.foto_url }} style={styles.avatarImage} contentFit="cover" />
                            ) : (
                                <Text style={styles.avatarFallback}>{opponentName[0]?.toUpperCase() || 'U'}</Text>
                            )}
                        </View>

                        <View style={styles.textBlock}>
                            <Text style={styles.title}>
                                {isReadyInvite
                                    ? `@${opponentName} está aguardando você`
                                    : `@${opponentName} aceitou seu desafio`}
                            </Text>
                            <Text style={styles.subtitle}>
                                {isReadyInvite
                                    ? 'Entre no duelo ao vivo agora ou desista da partida.'
                                    : 'Escolha se deseja duelar agora com seu oponente.'}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.actions}>
                        <TouchableOpacity style={styles.cancelButton} onPress={handleCancel} disabled={isCancelling || isJoining}>
                            {isCancelling ? (
                                <ActivityIndicator color="#FCA5A5" size="small" />
                            ) : (
                                <Text style={styles.cancelText}>Recusar</Text>
                            )}
                        </TouchableOpacity>

                        <TouchableOpacity style={styles.joinButtonWrap} onPress={handleJoin} disabled={isJoining || isCancelling}>
                            <LinearGradient colors={['#34D399', '#10B981']} style={styles.joinButton}>
                                {isJoining ? (
                                    <ActivityIndicator color="#FFFFFF" size="small" />
                                ) : (
                                    <Text style={styles.joinText}>Duelar ao Vivo</Text>
                                )}
                            </LinearGradient>
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </View>
        </Modal>
    );
});

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        justifyContent: 'flex-start',
        paddingTop: 42,
        paddingHorizontal: 14,
    },
    toast: {
        borderRadius: 20,
        overflow: 'hidden',
        padding: 16,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.14)',
        shadowColor: '#000000',
        shadowOpacity: 0.28,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
        elevation: 14,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#EF4444',
    },
    liveText: {
        color: '#FCA5A5',
        fontSize: 11,
        fontWeight: '900',
        letterSpacing: 1,
    },
    contentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    avatarWrap: {
        width: 52,
        height: 52,
        borderRadius: 26,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(168,85,247,0.25)',
        overflow: 'hidden',
    },
    avatarImage: {
        width: 52,
        height: 52,
    },
    avatarFallback: {
        color: '#FFFFFF',
        fontSize: 21,
        fontWeight: '900',
    },
    textBlock: {
        flex: 1,
    },
    title: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '900',
    },
    subtitle: {
        color: 'rgba(255,255,255,0.62)',
        fontSize: 13,
        marginTop: 4,
    },
    actions: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 16,
    },
    cancelButton: {
        flex: 0.35,
        height: 44,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(239,68,68,0.18)',
        borderWidth: 1,
        borderColor: 'rgba(239,68,68,0.32)',
    },
    cancelText: {
        color: '#FCA5A5',
        fontSize: 13,
        fontWeight: '900',
    },
    joinButtonWrap: {
        flex: 0.65,
        height: 44,
        borderRadius: 14,
        overflow: 'hidden',
    },
    joinButton: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    joinText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '900',
    },
});

export default LiveDuelInviteOverlay;
